import type { Translation } from '@/types/translation';

// Italian (IT) translation file.

const itTranslation: Translation = {
  common: {
    save: 'Salva',
    close: 'Chiudi',
    search: 'Cerca',
    back: 'Indietro',
    continue: 'Continua',
    cancel: 'Annulla',
    submit: 'Invia',
    confirm: 'Conferma',
    delete: 'Elimina',
    save_changes: 'Salva modifiche',
    loading: 'Caricamento...',
  },
  pages: {
    account: {
      tabs: {
        dashboard: 'Dashboard',
        fleet_overview: 'Panoramica flotte',
        unit_overview: 'Tutte le unità',
        campaigns: 'Campagne',
        members: 'Membri',
      },
      dashboard: {
        active_fault_codes: {
          title: 'Codici di errore attivi',
          units_title: 'Unità con codici di errore attivi',
        },
        connectivity: {
          title: 'Connettività',
          units_title: 'Stato della connettività',
        },
        fuel_consumption: {
          title: 'Consumo di carburante',
          details_title: 'Dettagli del consumo di carburante',
        },
      },
      fleet_overview: {
        pinned_fleets: 'Flotte fissate',
        other_fleets: 'Altre flotte',
        pin: 'Fissa flotta',
        unpin: 'Sblocca flotta',
        edit: 'Modifica flotta',
        remove: 'Rimuovi flotta',
      },
      unit_overview: {
        title: 'Unità',
        unverified_ownership: 'Proprietà non verificata',
        add_to_fleet: 'Aggiungi alla flotta',
      },
      campaigns: {
        active_table: {
          title: 'Campagne attive',
          download_campaigns: 'Scarica campagne (xlsx)',
          empty_state: {
            title: 'Non hai campagne attive per questa unità',
          },
        },
        performed_table: {
          title: 'Campagne eseguite',
          empty_state: {
            title: 'Nessuna campagna eseguita disponibile',
          },
        },
      },
      members: {
        title: 'Membri',
        remove_members_from_account: 'Rimuovi {{selectedRows}} membri dall’account',
      },
    },
    fleet: {
      tabs: {
        dashboard: 'Dashboard',
        unit_overview: 'Tutte le unità',
        campaigns: 'Campagne',
      },
      campaigns: {
        title: 'Campagne attive',
        download_campaigns: 'Scarica campagne (xlsx)',
        empty_state: {
          title: 'Non hai campagne attive per questa flotta',
        },
      },
    },
    unit: {
      tabs: {
        specifications: 'Specifiche',
        campaigns: 'Campagne',
        coverage: 'Garanzia e coperture',
        maintenance_history: 'Storico manutenzione',
        manuals_handbooks: 'Manuali e guide',
        diagnostics_log: 'Registro diagnostico',
        services: 'Servizi digitali',
        settings: 'Impostazioni',
      },
      spare_parts: 'Ricambi',
      manuals_selection_description: 'Per quale prodotto desideri trovare manuali e guide.',
      specifications: {
        title: 'Specifiche',
      },
      campaigns: {
        active_table: {
          title: 'Campagne attive',
          empty_state: {
            title: 'Non hai campagne attive per questa unità',
          },
        },
        performed_table: {
          title: 'Campagne eseguite',
          empty_state: {
            title: 'Nessuna campagna eseguita disponibile',
          },
        },
      },
      coverage: {
        cta: {
          heading: 'Informazioni su garanzia e coperture',
          message: 'Per domande su garanzia e coperture contatta il tuo concessionario Volvo Penta o OEM.',
          find_dealer: 'Trova il concessionario più vicino',
          learn_more: 'Scopri di più',
        },
        empty_state: {
          title: 'Nessuna garanzia e/o copertura trovata per questa unità',
        },
      },
      publications: {
        select_menu: {
          category: 'Categoria',
          designation: 'Designazione',
          product_type: 'Tipo di prodotto',
          language: 'Lingua',
        },
        empty_state: {
          title: 'Nessuna pubblicazione disponibile per questa unità',
        },
      },
      services: {
        title: 'Servizi disponibili',
        all_services: {
          title: 'Attiva tutti i servizi',
        },
        list: {
          ActiveMonitoring: {
            Marine: {
              title: 'Extended',
              description:
                'Ottieni tutti i vantaggi di Core, oltre ad avvisi automatici via email inviati direttamente al concessionario selezionato.',
              checklist: [
                'Avvisa immediatamente il tuo concessionario se il sistema rileva un codice di errore critico',
                'Consenti al tuo concessionario di preparare azioni correttive immediate',
              ],
            },
            Industrial: {
              title: 'Monitoraggio attivo',
              description:
                "Active Monitoring aiuta i clienti a mantenere in funzione i loro prodotti Volvo Penta rilevando i problemi in anticipo, supportando un servizio più rapido e offrendo informazioni chiare sullo stato e sull'utilizzo del prodotto.\n\nActive Monitoring abilita inoltre Remote Software Updates, un servizio per installare campagne software over-the-air quando preferisci.",
              checklist: [
                'Maggiore tempo di attività grazie ad avvisi tempestivi e supporto proattivo',
                'Servizio più rapido e meglio preparato grazie alle informazioni giuste disponibili in anticipo',
              ],
            },
          },
          ActiveMonitoringCore: {
            Marine: {
              title: 'Core',
              description:
                'Condividi i dati di base del veicolo con il tuo concessionario per ricevere un supporto più rapido durante la manutenzione o in caso di problemi imprevisti.',
              checklist: [
                'Consenti al tuo concessionario di monitorare lo stato del prodotto e tenere traccia delle ore di funzionamento',
                "Consenti al tuo concessionario di analizzare immediatamente i codici di errore per una pianificazione proattiva dell'assistenza",
              ],
            },
            Industrial: {
              title: 'Connected Services Core',
              description:
                'Core consente al concessionario Volvo Penta di monitorare il prodotto Volvo Penta. <ActiveMonitoringCore>Per ulteriori dettagli, consulta la descrizione del servizio per Connected Services Core.</ActiveMonitoringCore>',
              checklist: [],
            },
          },
          ActiveMonitoringEcWifi: {
            Marine: {
              title: 'Extended',
              description:
                'Consenti al tuo concessionario di monitorare i dati del veicolo e ricevere avvisi immediati sui codici di errore critici per preparare azioni correttive immediate.',
              checklist: [
                'Avvisa immediatamente il tuo concessionario se il sistema rileva un codice di errore critico',
                'Consenti al tuo concessionario di preparare azioni correttive immediate',
              ],
            },
            Industrial: {
              title: 'Extended',
              description:
                'Consenti al tuo concessionario di monitorare i dati del veicolo e ricevere avvisi immediati sui codici di errore critici per preparare azioni correttive immediate.',
              checklist: [
                'Avvisa immediatamente il tuo concessionario se il sistema rileva un codice di errore critico',
                'Consenti al tuo concessionario di preparare azioni correttive immediate',
              ],
            },
          },
          Penta_Position: {
            Marine: {
              title: 'Servizi di localizzazione',
              description:
                "Location Services aiuta i clienti a localizzare e gestire rapidamente i loro prodotti Volvo Penta connessi tramite una vista mappa in Volvo Penta Connect, semplificando la gestione dell'assistenza e la supervisione della flotta.",
              checklist: [
                'Migliore visibilità della flotta con una mappa che mostra la posizione più recente delle unità connesse',
                "Risposta di assistenza più rapida grazie al facile accesso alla posizione dell'unità, incluse ore di funzionamento e codici di errore",
              ],
            },
            Industrial: {
              title: 'Servizi di localizzazione',
              description:
                "Location Services aiuta i clienti a localizzare e gestire rapidamente i loro prodotti Volvo Penta connessi tramite una vista mappa in Volvo Penta Connect, semplificando la gestione dell'assistenza e la supervisione della flotta.",
              checklist: [
                'Migliore visibilità della flotta con una mappa che mostra la posizione più recente delle unità connesse',
                "Risposta di assistenza più rapida grazie al facile accesso alla posizione dell'unità, incluse ore di funzionamento e codici di errore",
              ],
            },
          },
        },
        request_sent: 'Richiesta inviata',
        empty_state: {
          title: 'Provisioning del servizio non disponibile',
          message: 'Il provisioning dei servizi digitali non è disponibile per questa unità.',
        },
        active_services_title: 'Servizi attivi',
        available_services_title: 'Servizi disponibili',
        activate_button: 'Attiva servizio',
        terminate_button: 'Termina',
        read_more_button: 'Leggi di più',
        requires_prefix: 'Richiede {{serviceName}}',
      },
      settings: {
        unit_settings: {
          title: 'Impostazioni unità',
          unit_name: 'Nome unità',
          home_port: 'Porto base',
          operating_location: 'Luogo di utilizzo',
          upload_image: 'Carica immagine',
          add_files: 'Aggiungi file',
          helperText: 'File .png o .jpg fino a 3 MB',
        },
        network_settings: {
          title: 'Impostazioni di rete',
          empty_state: {
            title: 'Nessuna rete attiva disponibile',
            message: 'Le impostazioni di rete per questa unità appariranno qui una volta configurate.',
          },
          updating_title: 'Aggiornamento delle impostazioni di rete',
          updating_description: 'Attendere mentre recuperiamo la configurazione di rete più recente.',
        },
      },
      maintenance: {
        service_protocol_for_chassi: 'Protocollo di servizio per telaio {{chassisNumber}}',
        download_pdf: 'Scarica PDF',
        service: 'Servizio {{type}}',
        default_service: 'Trasmissione {{chassisSeries}} {{chassisNumber}}',
        general_comment: 'Commento generale sul servizio',
        no_comment: 'Nessun commento generale per questo protocollo di servizio.',
        periodic_tag: 'Ogni {{months}} mesi / {{engineHours}} h',
        non_periodic_month_tag: 'A {{months}} mesi',
        non_periodic_hours_tag: 'A {{engineHours}} h',
        empty_state: {
          title: 'Nessuno storico di manutenzione disponibile',
          message:
            'I protocolli di servizio per questo motore appariranno qui una volta registrati da un concessionario Volvo Penta.',
        },
        unverified_ownership_notice:
          'Alcune informazioni potrebbero essere limitate poiché la proprietà di questa unità non è ancora stata verificata. Contatta il tuo concessionario Volvo Penta autorizzato per completare la verifica.',
      },
    },
    settings: {
      application_settings: 'Impostazioni applicazione',
      account_name: 'Nome account flotta',
      notifications: 'Notifiche',
      email: 'Email',
      email_notifications: 'Abilita notifiche email',
      sms: 'SMS',
      sms_notifications: 'Abilita notifiche SMS',
    },
    support: {
      title: 'Domande frequenti',
      description: 'Trova informazioni di contatto e informazioni generali su Volvo Penta Connect.',
      professional_support: {
        tag: 'Supporto professionale',
        title: 'Gli esperti definitivi - al tuo servizio',
        description:
          'Contatta un concessionario autorizzato Volvo Penta come primo punto di riferimento quando hai bisogno di assistenza per il servizio motore, riparazioni, pezzi di ricambio o altri supporti - per imbarcazioni da diporto e commerciali.',
      },
      emergency_support: {
        tag: 'Supporto di emergenza',
        title: 'Volvo Penta Action Service',
        description:
          "Un guasto al motore raramente accade, ma se succede, Action Service è lì per metterti in contatto con il partner Volvo Penta più vicino a te. Offriamo supporto in diverse lingue, 24 ore su 24, tutto l'anno.",
      },
      platform_support: {
        tag: 'Supporto della piattaforma',
        title: 'Supporto Volvo Penta Connect',
        description:
          'Contatta il nostro team dedicato per assistenza con il tuo account e le funzionalità di Volvo Penta Connect. Siamo qui per aiutarti a gestire i dati del motore e la connettività con facilità.',
      },
      getting_started: {
        title: 'Per iniziare',
        question_1: {
          title: 'Per chi è Volvo Penta Connect?',
          body: "Volvo Penta Connect è un portale clienti che ti offre una panoramica delle tue unità e l'accesso a informazioni rilevanti come manuali e guide, campagne, stato della garanzia e altro ancora.",
        },
        question_2: {
          title: 'Cosa devo fare per prima cosa per utilizzare questo prodotto?',
          body: "Devi registrare un'unità sul tuo account. Una volta aggiunta un'unità, puoi monitorare i dati dell'unità, installare aggiornamenti software over-the-air e accedere a manuali e guide collegati a quell'unità.",
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Aggiungi unità' },
        },
        question_3: {
          title: "Posso utilizzare questo prodotto senza aggiungere un'unità?",
          body: "No. Volvo Penta Connect è costruito attorno alle tue unità. Manuali, dati del motore e servizi digitali sono tutti collegati a un'unità specifica. Se non possiedi o non prevedi di registrare un'unità, questo prodotto non soddisferà le tue esigenze.",
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Aggiungi unità' },
        },
        question_4: {
          title: 'Come registro il mio motore?',
          body: "Vai all'elenco delle unità e seleziona Aggiungi unità. Cerca il numero di serie, quindi completa e invia il modulo di registrazione. Alcuni motori industriali più recenti sono dotati di un codice QR preinstallato sul motore. Se disponibile, puoi scansionare questo codice per avviare il processo di registrazione.",
        },
        question_5: {
          title: 'Quali informazioni devo fornire?',
          body: "Inizia registrando la tua unità in Volvo Penta Connect. Vai all'elenco delle unità e seleziona Aggiungi unità, quindi cerca il numero di serie dell'unità e completa il modulo di registrazione.<br /><br />Una volta registrata l'unità, avrai accesso a informazioni come manuali, pezzi di ricambio, campagne, stato della garanzia e servizi digitali collegati a quell'unità.",
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Aggiungi unità' },
        },
        question_6: {
          title: 'Cosa posso fare una volta registrata la mia unità?',
          body: "Con un'unità registrata, puoi:<br /><br /><ul><li>- Raggruppare le unità in flotte per tipo di veicolo o caso d'uso</li><li>- Monitorare lo stato dell'unità</li><li>- Installare aggiornamenti software over-the-air</li><li>- Accedere a manuali e guide specifici per la tua unità</li></ul>",
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Panoramica unità' },
        },
        question_7: {
          title: 'Dove posso trovare manuali e guide?',
          body: "I manuali e le guide diventano disponibili una volta aggiunta un'unità al tuo account, in modo da poter mostrare la documentazione corrispondente alla tua unità specifica.",
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Aggiungi unità' },
        },
        question_8: {
          title: 'Come posso aggiungere un utente al mio account della flotta?',
          body: "Per aggiungere un nuovo utente, dovrai invitarlo dalla pagina Membri.<br /><br /><ul><li>1. vai a Membri</li><li>2. Seleziona Aggiungi membro</li><li>3. Crea un link di invito e copialo</li><li>4. Condividi il link con la persona che vuoi invitare</li><li>5. Una volta accettato l'invito, assegna un ruolo al nuovo membro</li></ul><br/>Solo gli utenti con il permesso di gestire i membri possono inviare inviti e assegnare ruoli.",
          link: { href: 'https://www.volvopenta.com/connect/members', label: 'Membri' },
        },
        question_9: {
          title: "Perché la mia unità viene contrassegnata come 'Proprietario non verificato'?",
          body: 'Le unità appena registrate vengono temporaneamente contrassegnate come Proprietario non verificato fino a quando la proprietà non viene confermata.<br /><br />La proprietà può essere verificata da un concessionario Volvo Penta autorizzato o dal nostro team di supporto. Una volta verificata, il contrassegno verrà rimosso automaticamente.<br /><br />Se hai bisogno di assistenza per la verifica, contatta il tuo concessionario Volvo Penta autorizzato.',
        },
        question_10: {
          title: 'Perché non riesco a vedere le mie unità?',
          body: "Le tue unità potrebbero non apparire nel tuo account per diversi motivi. Controlla questi scenari comuni:<br /><br />Disallineamento email:<br/>Le tue unità sono collegate a un indirizzo email specifico. Se hai creato un account con un indirizzo diverso, le unità non saranno visibili. Puoi creare un nuovo account con l'email corretta o contattare un concessionario Volvo Penta autorizzato per aiutarti a registrare nuovamente la tua unità.<br /><br />Registrazione in sospeso:<br/>Se hai aggiunto una nuova unità industriale, potrebbe non essere ancora nei nostri archivi. Queste unità non verranno visualizzate in Volvo Penta Connect fino a quando il nostro team di supporto non completerà il processo di registrazione manuale.<br /><br />Ruoli dell'account:<br/>Il tuo accesso a unità specifiche dipende dal ruolo assegnato. Se ritieni che il tuo accesso sia limitato, contatta il tuo responsabile della flotta per assicurarti di avere il ruolo corretto nell'account.",
        },
      },
      connectivity_telematics: {
        title: 'Connettività e telematica',
        question_1: {
          title: 'Come posso sapere se la mia unità è connessa?',
          body: "Puoi vedere lo stato di connettività di un'unità accanto al suo nome nell'elenco delle unità e nella pagina dell'unità.<br /><br /><GreenDot /> – L'unità è online. I dati sono stati ricevuti nelle ultime 2 ore.<br /><br /><GreyDot /> – L'unità è offline. Nessun dato è stato ricevuto nelle ultime 2 ore.<br /><br /><YellowDot /> – La connettività è disponibile ma non completamente configurata.<br /><br />Nessun punto – L'unità non ha capacità di connettività. Potrebbe essere idonea per l'installazione di un gateway telematico (TGW) tramite un concessionario.<br /><br />Se la tua unità è offline, può comunque essere utilizzata normalmente, ma gli aggiornamenti dei dati riprenderanno una volta che l'unità si riconnetterà.",
        },
        question_2: {
          title: 'Perché la mia unità non mostra alcuno stato di connettività?',
          body: 'Le unità senza indicatore di stato non hanno un gateway telematico (TGW) installato o attivato. Queste unità possono essere connesse installando un TGW tramite un concessionario Volvo Penta.',
        },
        question_3: {
          title: "Cos'è un gateway telematico (TGW)?",
          body: "Un gateway telematico (TGW) è un dispositivo hardware installato sull'unità che consente la connettività e il trasferimento dei dati a Volvo Penta Connect.<br /><br />Senza un TGW, l'unità non può essere monitorata digitalmente.",
        },
        question_4: {
          title: 'Come posso connettere la mia unità a Volvo Penta Connect?',
          body: "Per connettere un'unità, devono essere completati i seguenti passaggi:<br /><br /><ul><li>- L'unità deve avere un TGW installato</li><li>- Il TGW deve essere attivato da un concessionario o OEM</li><li>- I servizi digitali richiesti devono essere abilitati</li><li>- Il proprietario deve approvare l'accordo di gestione dei dati</li></ul><br /><br />Una volta completati, l'unità apparirà con lo stato di connettività in Volvo Penta Connect.",
        },
        question_5: {
          title: "Quali smartphone/tablet sono compatibili con l'app Volvo Penta Connect?",
          body: 'Per i dispositivi iOS, la versione del sistema operativo deve essere iOS 17 o successiva. Per i dispositivi Android, la versione del sistema operativo deve essere 9.0 o successiva.',
        },
        question_6: {
          title: 'Chi può aiutarmi a installare o attivare la connettività?',
          body: "Un concessionario Volvo Penta può aiutare con l'installazione del TGW, l'attivazione e la configurazione dei servizi.",
          link: {
            href: 'https://www.volvopenta.com/about-us/dealer-locator/',
            label: 'Trova un concessionario Volvo Penta',
          },
        },
        question_7: {
          title: 'Come configuro il Wi-Fi per la mia unità?',
          body: "Puoi configurare la tua connessione Wi-Fi direttamente nell'applicazione. Segui questi passaggi per iniziare:<br /><br /><ul><li>- **Vai alle impostazioni:** Accedi alla sezione di configurazione Wi-Fi nella tua dashboard.</li><li>- **Segui la guida:** Utilizza il manuale di istruzioni (PDF) per indicazioni passo dopo passo per completare la configurazione.</li><li>- **Connetti la tua unità:** Una volta configurata, la tua unità rimarrà automaticamente connessa al tuo spazio di lavoro digitale.</li></ul>",
          link: {
            href: 'https://www.volvopenta.com/connect/documents/Connect-to-WiFi-onboard.pdf',
            label: 'Manuale di istruzioni',
            icon: 'import',
          },
        },
      },
      campaigns_software_updates: {
        title: 'Campagne e aggiornamenti software',
        question_1: {
          title: "Cos'è una campagna?",
          body: "Una campagna è un'azione avviata da Volvo Penta per garantire la qualità del prodotto. Se una campagna riguarda una delle tue unità registrate, verrai notificato e guidato attraverso i passaggi necessari.",
          link: {
            href: 'https://www.volvopenta.com/connect/campaigns',
            label: 'Panoramica delle campagne',
          },
        },
        question_2: {
          title: 'Cosa devo fare quando ho una campagna attiva?',
          body: "Dipende dal tipo di campagna:<br /><br /><ul><li>- **Richiamo / Qualità / Servizio:** Contatta il tuo concessionario Volvo Penta locale</li><li>- **Installazione / OTA:** Queste campagne possono essere completate over the air utilizzando l'app Volvo Penta. Scarica l'app e segui le istruzioni all'interno dell'app.</li></ul>",
          link: {
            href: 'https://www.volvopenta.com/about-us/dealer-locator/',
            label: 'Trova il tuo concessionario',
          },
          ios: { href: '#', label: 'App store' },
          android: { href: '#', label: 'Google play' },
        },
        question_3: {
          title: 'Cosa devo fare prima di installare una campagna over-the-air (OTA)?',
          body: "Prima di iniziare un'installazione OTA, assicurati che:<br /><br /><ul><li>- Hai selezionato l'unità corretta</li><li>- Il Bluetooth è abilitato sul tuo dispositivo</li><li>- Sei entro 3 metri dall'unità</li><li>- Tutte le condizioni preliminari richieste mostrate nel prodotto sono soddisfatte</li><li>- L'unità rimane alimentata e indisturbata durante l'installazione</li></ul><br/>Se una qualsiasi condizione non è soddisfatta, l'installazione non inizierà.",
        },
        question_4: {
          title: "Sto avendo problemi nell'installare una campagna over-the-air (OTA).",
          body: "Se l'installazione fallisce o non inizia:<br /><br /><ul><li>- Verifica che il Bluetooth sia abilitato e che tu sia vicino all'unità</li><li>- Rimani entro 3 metri per l'intera installazione</li><li>- Riavvia l'app e riprova</li><li>- Ricontrolla che tutte le condizioni preliminari dell'unità siano soddisfatte</li></ul><br/>Se il problema persiste, contatta il supporto.",
        },
        question_5: {
          title: 'Dove posso vedere le installazioni over-the-air (OTA) completate?',
          body: "Puoi visualizzare le installazioni OTA completate nella pagina dell'unità. Apri l'unità e vai alla scheda Campagne per vedere l'intera cronologia delle installazioni.",
        },
        question_6: {
          title: "Una scarsa connettività può influire su un'installazione OTA?",
          body: "Sì. Un'installazione OTA può fallire se l'unità ha una connettività cellulare o Wi-Fi instabile o assente. Assicurati che l'unità abbia una connessione affidabile e rimanga connessa fino al completamento dell'installazione.",
        },
        question_7: {
          title: 'Perché la mia unità deve essere regolarmente connessa?',
          body: "La connettività regolare aiuta a garantire che la tua unità sia pronta per gli aggiornamenti software. Se un'unità non è stata connessa per un po' di tempo, potrebbero mancare dati o preparazioni necessarie, il che può ritardare o bloccare un'installazione OTA.",
        },
        question_8: {
          title: 'Cosa succede se non completo una campagna in tempo?',
          body: 'Le campagne non vengono installate automaticamente. Se una campagna non viene completata entro il periodo di tempo specificato, potrebbe non essere più possibile eseguirla. Segui sempre le istruzioni mostrate e completa la campagna entro il periodo indicato.',
        },
      },
      maintenance_service: {
        title: 'Manutenzione e servizio',
        question_1: {
          title: 'Posso vedere la cronologia della manutenzione della mia unità?',
          body: "Sì. Ogni unità ha una scheda Cronologia manutenzione in cui sono elencati i servizi completati. Vengono mostrati solo i servizi eseguiti dai concessionari autorizzati Volvo Penta. Se manca un servizio, contatta direttamente il tuo concessionario.<br /><br />Seleziona un'unità e apri la scheda Cronologia manutenzione per visualizzare i dettagli.",
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Panoramica unità' },
        },
        question_2: {
          title: 'Perché non posso vedere quale concessionario ha eseguito un servizio?',
          body: "I dettagli del concessionario sono mostrati solo ai proprietari verificati dell'unità.<br /><br />Per visualizzare il nome e la posizione del concessionario per un servizio completato, è necessario essere registrati come proprietario. Se non sei ancora verificato, contatta il tuo concessionario autorizzato Volvo Penta per confermare la tua proprietà.",
        },
      },
      warranty_coverage: {
        title: 'Garanzia e copertura estesa',
        question_1: {
          title: 'Come posso sapere se la mia unità ha garanzia e copertura?',
          body: "Ogni unità ha una scheda Garanzia e copertura che mostra eventuali garanzie attive. Seleziona un'unità e apri la scheda per vedere i dettagli.",
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Panoramica unità' },
        },
        question_2: {
          title: "Cos'è la garanzia standard?",
          body: 'Tutti i prodotti Volvo Penta includono una garanzia standard di fabbrica. La copertura si applica per un periodo di tempo definito o un numero di ore di funzionamento, a seconda di quale condizione si verifichi per prima, e dipende dal prodotto e dalla sua applicazione.',
        },
        question_3: {
          title: "Cos'è la garanzia illimitata standard?",
          body: "Alcuni tipi di installazione non hanno limiti di ore di funzionamento durante il primo e/o secondo anno di copertura della garanzia. I dettagli dipendono dal prodotto e dall'installazione e sono definiti nei termini della garanzia.",
        },
        question_4: {
          title: "Cos'è la garanzia dei componenti?",
          body: 'La garanzia dei componenti copre componenti principali specifici del tuo prodotto Volvo Penta. La durata della copertura e i limiti di ore di funzionamento variano a seconda del componente e sono definiti nei termini della garanzia.',
        },
        question_5: {
          title: "Cos'è la garanzia sulle emissioni?",
          body: 'I motori approvati secondo le normative US EPA o California ARB includono la copertura della garanzia sulle emissioni. Questa si applica per un periodo di tempo definito o un numero di ore di funzionamento, come indicato nei termini della garanzia sulle emissioni.',
        },
      },
      notification: {
        title: 'Notifiche',
        question_1: {
          title: 'Perché non ricevo notifiche sulle mie unità?',
          body: 'Controlla le impostazioni delle notifiche. Assicurati che le notifiche siano abilitate nella pagina Impostazioni.',
          link: { href: 'https://www.volvopenta.com/connect/settings', label: 'Impostazioni' },
        },
      },
    },
    login: {
      hero_title: 'Benvenuto in Volvo Penta Connect',
      hero_body:
        'Tutto ciò che riguarda il tuo motore o prodotto Volvo Penta in un unico posto. Manuali, guide, ricambi, richiami, campagne qualità, stato della garanzia e altro ancora.',
      log_in: 'Accedi',
      blurb_back_button: 'Home',
      blurb_bolded_main_text:
        'Benvenuto in Volvo Penta Connect, la nuova piattaforma per proprietari di flotte e di imbarcazioni da diporto.',
      blurb_regular_main_text:
        'Registrando il tuo motore puoi accedere a informazioni rilevanti come manuali, richiami e stato della garanzia. La registrazione garantisce che il tuo concessionario locale sia pronto a supportarti con i ricambi e i servizi corretti. Il tutto, completamente gratis.',
      CTAs: {
        manuals_label: 'Accesso pratico ai manuali',
        manuals_title: 'Tutti i tuoi manuali a portata di clic',
        manuals_body:
          'Con Volvo Penta Connect non devi più cercare il numero di serie per accedere ai manuali. La nostra piattaforma offre una soluzione con un solo clic, fornendoti l’intera raccolta di manuali e guide per tutti i tuoi motori e prodotti.',
        efficiency_label: 'Efficienza',
        efficiency_title: 'Trova rapidamente i ricambi giusti',
        efficiency_body:
          'Volvo Penta Connect offre una funzionalità semplice da usare che semplifica la ricerca del ricambio corretto per il tuo motore Volvo Penta, senza la necessità di cercare il numero di serie o il modello del motore.',
        service_label: 'Monitora la manutenzione del prodotto',
        service_title: 'Visualizza lo storico dei servizi registrati per i tuoi prodotti',
        service_body:
          'Rimani aggiornato sullo stato di salute dei tuoi prodotti con Volvo Penta Connect. Ottieni una panoramica completa, inclusi i dettagli degli interventi, dello storico dei servizi registrati per tutti i tuoi prodotti Penta.',
      },
      cards: {
        uptime_label: 'Garantisci l’operatività',
        uptime_title: 'Scopri se hai campagne attive',
        uptime_body:
          'Con Volvo Penta Connect tenere traccia delle campagne sui tuoi prodotti non è mai stato così semplice. Assicurati che i tuoi prodotti siano aggiornati e sempre nelle migliori condizioni.',
        connect_label: 'Tieni sotto controllo tutte le tue garanzie',
        connect_title: 'Le tue garanzie in Volvo Penta Connect',
        connect_body:
          'Ottieni una panoramica di tutte le tue garanzie e coperture. Verifica se sono attive e per quanto tempo saranno valide, e ricevi notifiche quando stanno per scadere.',
      },
    },
    welcome: {
      title: 'Benvenuto!',
      body: 'Per sfruttare appieno Volvo Penta Connect, è necessario registrare una imbarcazione o una macchina sul proprio account. Una volta aggiunta, avrai accesso a manuali e guide, un accesso più facile ai ricambi pertinenti, agli stati di garanzia e molto altro ancora.',
      get_help: 'Ottieni aiuto',
      get_started: 'Inizia',
      accordion: {
        who_is_it_for: {
          title: 'Per chi è Volvo Penta Connect?',
          body: 'Volvo Penta Connect è un portale clienti dove puoi ottenere una panoramica delle tue unità e delle informazioni pertinenti come manuali e guide, campagne, stato della garanzia e altro ancora.',
        },
        how_to_register: {
          title: 'Posso utilizzare questo prodotto senza registrare un’unità?',
          body: 'Devi registrare un’unità sul tuo account. Una volta aggiunta un’unità, puoi monitorare i dati dell’unità, installare aggiornamenti software over-the-air e accedere ai manuali e alle guide collegati a quell’unità.',
        },
        can_i_use_without_registering: {
          title: 'Dove posso trovare manuali e guide?',
          body: 'I manuali e le guide diventano disponibili una volta aggiunta un’unità al tuo account, in modo da poter mostrare la documentazione corrispondente alla tua unità specifica.',
        },
      },
    },
  },
  components: {
    modal: {
      accept_invitation_modal: {
        invite_title: 'Benvenuto in {{name}}!',
        invite_description:
          'Sei stato invitato allo spazio di lavoro {{fleetName}}. Qui puoi monitorare la salute della flotta e delle unità, tenere traccia delle analisi e ricevere notifiche sugli aggiornamenti di campagne e garanzie.',
        accept: "Accetta l'invito",
        dismiss: "Rifiuta l'invito",
      },
      add_units_fleet_modal: {
        button_add: 'Crea flotta',
        button_update: 'Aggiorna flotta',
        title_add: 'Crea flotta',
        title_update: 'Aggiorna flotta',
        title_success: 'Flotta creata con successo',
        text_success: 'La tua flotta è stata creata. Se la nuova flotta non appare nell’elenco, aggiorna la pagina.',
        fleet_name: 'Inserisci il nome della flotta o seleziona la tua flotta',
        select_your_fleet: 'Seleziona la tua flotta',
        click_to_create_fleet: 'Clicca per creare una flotta',
        table: {
          unit_name: 'Nome unità',
          location: 'Località',
          engines: 'Motori',
          fleets: 'Flotte',
        },
        cancel: 'Salta per ora',
        submit: {
          add_units: 'Aggiungi unità alla flotta',
          create_fleet: 'Crea flotta',
          close: 'Chiudi',
        },
      },
      remove_units_fleet_modal: {
        button_remove: 'Rimuovi dalla flotta',
        title_remove: 'Rimuovi unità dalla flotta',
        description: 'Stai per rimuovere le unità selezionate dalla flotta. Questa azione non può essere annullata.',
        confirmation: 'Sei sicuro di voler rimuovere le unità selezionate dalla flotta?',
        cancel: 'Annulla',
        submit: {
          create_fleet: 'Rimuovi dalla flotta',
        },
      },
      delete_fleet_modal: {
        title: 'Rimuovi flotta',
        description:
          'La rimozione di una flotta dal tuo account è permanente. Puoi comunque aggiungere nuovamente una flotta in qualsiasi momento.',
        confirmation: 'Sei sicuro di voler rimuovere la seguente flotta?',
        submit: 'Sì, rimuovi la flotta dall’account',
      },
      edit_fleet_modal: {
        title: 'Modifica flotta',
        fleet_name: 'Nome flotta',
        select_icon: 'Seleziona icona',
      },
      add_new_unit_modal: {
        button: 'Aggiungi unità',
        search_unit: {
          title: 'Trova la tua unità',
          description: "Cerca l'unità che vuoi aggiungere utilizzando il numero di serie del motore.",
          serial_number: 'Numero di serie del motore',
          find_serial_number: 'Come trovare il numero di serie',
          is_not_found: {
            label: 'Il numero di serie non esiste nei nostri archivi',
            body: 'Assicurati di aver trovato il numero di serie corretto. Se ancora non funziona, contatta il tuo concessionario Volvo Penta o trovane uno nel nostro <a>Trova Concessionario</a> per ricevere assistenza.',
          },
          is_not_registered_industrial: {
            label: 'Unità non registrata per questo prodotto',
            body: "Registra l'unità per aggiungerla al tuo account. La richiesta di registrazione sarà gestita dal nostro team di supporto e l'unità verrà aggiunta al termine della procedura.",
          },
          is_not_registered_marine: {
            label: 'Unità non registrata per questo prodotto',
            body: "L'unità deve essere registrata per poterla aggiungere al tuo account. Contatta il tuo concessionario Volvo Penta o trovane uno nel nostro <a>Trova Concessionario</a> per ricevere assistenza con la registrazione dell'unità.",
          },
          is_maximum_units_reached: {
            label: 'Numero massimo di unità raggiunto',
            body: "Puoi aggiungere fino a tre unità non di proprietà. Per aggiungerne altre, devi essere registrato come proprietario dell'unità. Contatta il tuo concessionario Volvo Penta per assistenza o utilizza il nostro Localizzatore di concessionari per trovarne uno vicino a te.",
          },
          is_unit_already_added: {
            label: 'Unità già aggiunta al tuo account',
            body: 'Questa unità è già disponibile nel tuo account e non necessita di essere registrata nuovamente. Puoi trovarla nella tua lista di unità.',
          },
        },
        unit_information: {
          title: 'Inserisci informazioni unità',
          description:
            'Compila i dettagli sottostanti per aggiungere la tua imbarcazione a Volvo Penta Connect e ricevere informazioni rilevanti sul tuo prodotto Penta.',
          privacy_information:
            "Volvo Penta registra e utilizza alcuni dati personali che ti riguardano. Se desideri saperne di più su come Volvo Penta tratta i tuoi dati personali, visita <a>Informazioni sulla privacy per la registrazione del proprietario o dell'operatore</a>.",
        },
        marine_unit_information: {
          serial_number: 'Numero di serie del motore',
          serial_number_helperText: 'Il numero di serie Volvo Penta si trova sulla targa del motore.',
          vessel_name: 'Nome imbarcazione',
          vessel_name_helperText: 'Il nome (es. Black Pearl) dato alla tua imbarcazione.',
          vessel_id: 'ID imbarcazione',
          imo_number: 'Numero IMO',
          imo_number_helperText:
            "Numero dell'Organizzazione Marittima Internazionale che identifica in modo univoco la tua imbarcazione.",
          mmsi_number: 'Numero MMSI',
          mmsi_number_helperText:
            'Numero di identità del servizio mobile marittimo utilizzato per le comunicazioni radio.',
          manufacturer: 'Produttore',
          manufacturer_helperText: 'Il produttore della tua imbarcazione.',
          model: 'Modello',
          model_helperText: "Il modello dell'imbarcazione.",
          vessel_segment: 'Segmento imbarcazione',
          type_of_vessel: 'Tipo di imbarcazione',
          home_port: 'Porto base',
          home_port_helperText: 'Sito o porto base della tua imbarcazione',
          add_unit: 'Aggiungi unità',
        },
        marine_unit_added: {
          title: 'La tua unità è stata aggiunta',
          description:
            "La tua imbarcazione è stata aggiunta e potrai visualizzare le informazioni dell'imbarcazione, scaricare manuali, trovare ricambi e altro ancora.",
          add_another_unit: "Aggiungi un'altra unità",
          go_to_unit: "Vai all'unità",
        },
        industrial_contact_information: {
          title: 'Inserisci informazioni di contatto',
          description:
            'Compila i dettagli del proprietario qui sotto per aggiungere la tua unità. I dettagli inseriti saranno controllati manualmente dal nostro team di supporto prima di registrarti come proprietario.',
          proof_of_ownership: 'Prova di proprietà',
          proof_of_ownership_helperText:
            "È richiesta una prova di proprietà (ad es. contratto, ricevuta o conferma d'ordine) per registrare l'azienda come proprietaria della macchina. Se non puoi fornire la prova di proprietà, contatta il tuo concessionario.",
          proof_of_ownership_upload:
            'Trascina e rilascia o fai clic per sfogliare i file, PDF, png, jpg o heic di massimo 3 MB ciascuno.',
          proof_of_ownership_qr: 'Proprietà confermata tramite codice QR',
          contact_person: 'Persona di contatto',
          contact_person_description: "Sarai registrato come persona di contatto per l'azienda e questa macchina.",
          first_name: 'Nome',
          last_name: 'Cognome',
          email: 'Email',
          company_details: "Dettagli dell'azienda",
          company_details_description: "Inserisci le informazioni dell'azienda proprietaria della macchina.",
          company_name: 'Nome azienda',
          company_name_helperText: "L'azienda proprietaria della macchina.",
          vat_number: 'Partita IVA',
          address_line: 'Indirizzo',
          zip_code: 'CAP',
          city: 'Città',
          state: 'Stato / Provincia',
          country: 'Paese',
          notification: 'Preferenze di notifica',
        },
        industrial_unit_information: {
          engine_serial_number: 'Numero di serie del motore',
          engine_serial_number_helperText:
            'Se la tua macchina ha più di un motore, fai clic per aggiungere un altro campo e inserire il numero di serie del motore aggiuntivo.',
          add_engine: 'Aggiungi motore',
          machine_name: 'Nome macchina',
          machine_name_helperText: 'Il nome (es. T4, Unità 11) della tua macchina, veicolo o generatore di energia.',
          machine_id: 'ID macchina',
          machine_id_helperText: 'Il numero di identificazione univoco fornito dal produttore originale (OEM).',
          manufacturer: 'Produttore',
          manufacturer_helperText:
            'Il produttore della tua macchina, veicolo o generatore di energia (es. Magni, Inmesol, Atlas).',
          model: 'Modello',
          model_helperText: 'Il modello della tua macchina, veicolo o generatore di energia.',
          machine_segment: 'Segmento macchina',
          machine_segment_helperText:
            'Il segmento in cui si trova la tua macchina (es. movimentazione materiali, estrazione mineraria, silvicoltura).',
          type_of_machine: 'Tipo di macchina',
          type_of_machine_helperText:
            'Che tipo di macchina è (es. trattore terminale, mietitrice, carrello elevatore).',
          operating_location: 'Luogo di utilizzo',
          operating_location_helperText: 'Sito o luogo di utilizzo della tua macchina.',
        },
        industrial_unit_added: {
          title: 'Unità aggiunta',
          description:
            'La tua macchina è stata aggiunta e potrai visualizzare le informazioni della macchina, scaricare manuali, trovare ricambi e altro ancora.',
          manual_ownership_registration: 'Registrazione manuale della proprietà',
          manual_ownership_registration_description:
            "La registrazione e il controllo della proprietà vengono effettuati manualmente dal nostro team di supporto. <b>Attualmente stiamo affrontando un'elevata richiesta, il che comporta lunghi tempi di elaborazione, prevedi tra 2-6 settimane.</b> Riceverai un'email di conferma quando la tua richiesta sarà approvata.",
          add_another_unit: "Aggiungi un'altra unità",
          go_to_unit: "Vai all'unità",
        },
        industrial_no_unit_added: {
          title: 'La macchina non verrà visualizzata in Volvo Penta Connect',
          description:
            'La tua macchina non esiste nei nostri archivi. Non verrà visualizzata in Volvo Penta Connect fino al completamento del processo di registrazione manuale da parte del nostro team di supporto.',
          manual_registration: 'Registrazione manuale',
          manual_registration_description:
            "La registrazione e il controllo della proprietà vengono effettuati manualmente dal nostro team di supporto. <b>Attualmente stiamo affrontando un'elevata richiesta, il che comporta lunghi tempi di elaborazione, prevedi tra 2-6 settimane.</b> Riceverai un'email di conferma quando la tua richiesta sarà approvata.",
          manuals_campaigns: 'Manuali, ricambi e campagne',
          manuals_campaigns_description:
            'Se stai cercando manuali e guide, ricambi o campagne - qui trovi i link dove puoi ottenerli fino al completamento della registrazione. Hai anche ricevuto queste informazioni nella tua casella email.',
          add_another_unit: "Aggiungi un'altra unità",
        },
      },
      invite_members_modal: {
        button: 'Aggiungi membri',
        title: 'Link di invito in sospeso',
        empty_state: {
          title: 'Nessun invito trovato.',
          message: 'Prova a creare e copiare un invito negli appunti con il pulsante qui sotto.',
        },
        generate_invitation_link: 'Genera e copia un nuovo link di invito',
      },
      remove_members_modal: {
        title: 'Rimuovi membro/i',
        description:
          'La rimozione di un membro dall’account {{count}} è permanente. Puoi comunque reinvitare il membro in qualsiasi momento.',
        are_you_sure: 'Sei sicuro di voler rimuovere i seguenti membri?',
        cancel: 'Annulla',
        submit: 'Sì, rimuovi i membri dall’account',
      },
      add_network_modal: {
        button: 'Aggiungi rete',
        title: 'Aggiungi rete',
        refresh_network: 'Aggiorna rete',
        edit_network: 'Modifica rete',
        remove_network: 'Rimuovi rete',
        remove_network_description:
          "Rimuovi la rete dall'elenco salvato per questo veicolo / imbarcazione. La rimozione della rete è permanente. Puoi sempre aggiungere nuovamente la rete.",
        network_name: 'Nome rete (SSID)',
        network_name_helperText: 'ID rete del router wifi',
        password: 'Password',
        password_helperText: 'Password del router wifi',
        error_title: 'Qualcosa è andato storto',
        error_description: 'Impossibile aggiornare la rete. Riprova più tardi.',
        priority: 'Priorità',
      },
      wifi_polling_modal: {
        title: 'Updating Network Settings',
        title_error: 'Network Settings Update',
        connecting: 'Connecting to your device...',
        searching: 'Searching for your network settings...',
        almost: 'Almost there, fetching configuration...',
        hang_tight: 'Still working on it, hang tight...',
        communicating: 'Communicating with the gateway...',
        moment: 'Just a moment longer...',
        timeout: 'Unable to reach your device. Please try again later.',
        error: 'Something went wrong while updating network settings. Please try again.',
      },
      wifi_error_log_modal: {
        title: 'Registro errori',
        no_errors: 'Nessun errore trovato',
        close: 'Chiudi',
      },
      digital_services_modal: {
        activate_service_content: {
          title: 'Attiva servizio',
          description:
            'Per aiutarci a preparare il contratto di attivazione corretto e a richiedere i recapiti giusti, specifica per conto di chi stai attivando questo servizio.',
          myself: 'Me stesso',
          myself_helper: 'Attiva questo servizio per la tua macchina o imbarcazione personale.',
          company: "Un'azienda",
          company_helper: "Attiva questo servizio per conto di un'azienda, una flotta o un'organizzazione.",
        },
        contract_details_content: {
          title: 'Dettagli del contratto',
          first_name: 'Nome',
          last_name: 'Cognome',
          email_address: 'Indirizzo email',
          company_name: "Nome dell'azienda",
          company_details_title: "Dettagli dell'azienda",
          company_details_description: "Inserisci le informazioni dell'azienda proprietaria della macchina.",
          vat_number: 'Partita IVA',
          social_security_number: 'Numero di sicurezza sociale',
          state_province: 'Stato/Provincia',
          country: 'Paese',
          address: 'Indirizzo',
          city: 'Città',
          postal_code: 'Codice postale',
          is_correct_info: 'Confermo che le informazioni fornite sopra sono veritiere e corrette.',
          is_terms_checked:
            "Riconosco e confermo di aver letto e compreso i termini e le condizioni dell'Accordo sui Servizi Digitali (incluse le appendici) e di essere autorizzato ad accettare il suddetto <a>contratto</a> con effetto legalmente vincolante.",
          data_management_agreement: "Ho anche letto e compreso l'<a>Accordo di Gestione dei Dati</a>.",
          remote_software_updates_title: 'Remote Software Updates',
          service_description_for: 'Descrizione del servizio per {{serviceName}}',
          is_service_details_checked:
            'Riconosco e confermo di aver letto e compreso il <serviceDescription /> e di essere autorizzato ad accettare i suddetti dettagli del servizio.',
          is_data_act_checked:
            "La legge europea sui dati richiede che ti forniamo informazioni precontrattuali che descrivono come un Prodotto Volvo Penta Connesso (immesso sul mercato in uno Stato membro dell'UE) genera dati di prodotto, come i dati di prodotto vengono raccolti, come puoi accedervi come utente e come vengono utilizzati da Volvo Penta. Le informazioni complete sono disponibili su <a>EU Data Act | Volvo Penta</a>. Selezionando questa casella e procedendo con l'acquisto, il noleggio o il leasing del Prodotto Volvo Penta Connesso, riconosci di aver avuto accesso a queste informazioni precontrattuali come richiesto dalla legge europea sui dati.",
        },
        choose_receiver_content: {
          title: 'Scegli un concessionario',
          description:
            'Si prega di aggiungere un concessionario per ricevere le notifiche di allerta. Questo garantisce il massimo tempo di attività e la pianificazione del servizio più efficiente per il tuo prodotto Volvo Penta.',
          find_nearest_dealer: 'Trova il concessionario più vicino',
          dealer: 'Concessionario',
          dealer_placeholder: 'Inserisci il nome del concessionario e/o il suo indirizzo email',
        },
        thanks_content: {
          title: 'Grazie!',
          description:
            "Si prega di notare che l'attivazione non è immediata e richiederà del tempo. Ti informeremo via email quando il servizio sarà pronto all'uso.",
          description_all_services: "Ti informeremo via email una volta che i servizi saranno pronti all'uso.",
          Marine: {
            description:
              "Si prega di notare che l'attivazione non è immediata e potrebbe richiedere del tempo. Ti informeremo via email una volta completata l'attivazione.",
          },
        },
        deactivate_service: {
          title: 'Termina servizio',
          description: 'Sei sicuro di voler terminare il servizio?',
          description_all: 'Sei sicuro di voler terminare tutti i servizi, ({{serviceNames}})?',
          deactivate: 'Termina',
        },
      },
    },
    table: {
      items_selected: '{{selectedRowsCount}} elementi selezionati',
      page: 'Pagina {{currentPage}} di {{totalPages}}',
      unit_table: {
        unit_name: 'Nome unità',
        model_brand: 'Modello/marca',
        location: 'Località',
        engines: 'Motori',
        operating_hours: 'Ore di esercizio',
        fleets: 'Flotte',
        campaigns: 'Campagne',
        digital_services: 'Servizi digitali',
        unverified_ownership: 'Proprietà non verificata',
      },
      campaigns_table: {
        campaign: 'Campagna',
        download: 'Scarica',
        created_date: 'Data di creazione',
        valid_to: 'Valida fino al',
        product: 'Prodotto',
        type: 'Tipo',
      },
      members_table: {
        name: 'Nome',
        email: 'Email',
        account: 'Account',
      },
      invitations_table: {
        creation_date: 'Data di creazione',
      },
      specifikationer_table: {
        driveline: 'Trasmissione',
        product_type: 'Tipo di prodotto',
        product: 'Prodotto',
        serial_number: 'Numero di serie',
        operating_hours: 'Ore di esercizio',
        engine: 'Motore',
        segment: 'Segmento',
        rated_power: 'Potenza nominale',
        rated_speed: 'Velocità nominale',
      },
      network_settings_table: {
        priority: 'Priorità',
        ssid: 'SSID',
        frequency: 'Frequenza',
        last_updated: 'Ultimo aggiornamento',
        connection: 'Connessione',
        connected: 'Connesso',
        not_connected: 'Non connesso',
        lost_connection: 'Connessione persa',
        disconnected: 'Disconnesso',
      },
      coverage_table: {
        type: 'Tipo',
        description: 'Descrizione garanzia/copertura',
        expiration_hours: 'Ore di scadenza',
        expiration_date: 'Data di scadenza',
      },
      fault_codes_table: {
        unit: 'Unità',
        fault_code: 'Codice di errore',
        fault_description: 'Descrizione',
        failure_event: 'Evento di guasto',
        timestamp: 'Data e ora',
        operating_hours: 'Ore di esercizio',
        occurrences: 'Occorrenze',
        severity: 'Gravità',
      },
      connectivity_table: {
        unit: 'Unità',
        operating_hours: 'Ore di esercizio',
        last_seen: 'Ultimo visto',
        status: 'Stato della connettività',
      },
      fuel_consumption_table: {
        unit: 'Unità',
        chassis_id: 'ID telaio',
        operating_hours: 'Ore di esercizio (h)',
        fuel_efficiency: 'Efficienza del carburante (l/h)',
        total_fuel: 'Consumo totale di carburante (l)',
      },
      publications_table: {
        title: 'Titolo',
        description: 'Descrizione',
        designation: 'Prodotto',
        product: 'Tipo di prodotto',
        size: 'Dimensione',
        languageCode: 'Lingua',
        buy: 'Acquista',
      },
      maintenance_dealer_table: {
        header: 'Concessionario',
        dealer_name: 'Nome del concessionario',
        location: 'Località',
      },
      maintenance_protocol_details_table: {
        header: 'Dettagli del protocollo di servizio',
        title: 'Titolo',
        current_service: 'Servizio corrente',
        previous_service: 'Servizio precedente',
        date: 'Data',
        operating_hours: 'Ore di esercizio',
        unit_age: "Età dell'unità",
        months: 'mesi',
      },
      maintenance_driveline_table: {
        header: 'Linea di trasmissione {{driveline}}',
        name: 'Nome',
        serial_number: 'Numero di serie',
        product: 'Prodotto',
      },
      maintenance_service_table: {
        header: 'Servizio {{service}}',
        activity: 'Attività',
        detail: 'Dettaglio',
        last_performed: 'Ultima esecuzione',
        performed: 'Eseguito',
        complete: 'Completato',
        not_performed: 'Non eseguito',
      },
    },
    tooltip: {
      copy_clipboard: 'Copia negli appunti',
    },
    language_selector: {
      de: 'Sprache - Deutsch',
      en: 'Language - English',
      fr: 'Langue - Français',
      it: 'Lingua - Italiano',
      nl: 'Taal - Nederlands',
      pt: 'Idioma - Português',
      sv: 'Språk - Svenska',
    },
    main_menu: {
      home: 'Home',
      settings: 'Impostazioni',
      support: 'Supporto',
    },
    user_menu: {
      title: 'Menu utente',
      account_settings: 'Impostazioni account',
      logout: 'Disconnetti',
      switch_fleet_account: 'Cambia account flotta',
    },
    footer: {
      useful: 'Utili',
      manuals_and_handbooks: 'Manuali e guide',
      dealer_locator: 'Trova concessionario',
      warranty: 'Garanzia',
      parts_column: 'Ricambi e accessori',
      parts: 'Ricambi',
      remanufactured: 'Componenti e motori rigenerati',
      contact: 'Contatti',
      action_service: 'Volvo Penta Action Service',
      faq: 'Domande frequenti',
      contact_us: 'Contattaci',
      privacy_policy: 'Informativa sulla privacy',
      cookie_policy: 'Informativa sui cookie',
    },
    cta: {
      members_fleetaccount_cta: {
        heading: 'Invita altri al tuo account',
        message:
          "Aggiungi membri per condividere l'accesso alle tue unità e flotte. Puoi invitare colleghi, partner commerciali o membri della famiglia e assegnare ruoli per controllare cosa possono vedere e gestire. Copia il link e invialo al tuo membro.",
      },
      members_digitalaccount_cta: {
        heading: 'Invita altri al tuo account',
        message:
          "L'invito di membri richiede un proprietario dell'unità verificato. Una volta confermata la tua proprietà, potrai invitare colleghi, partner commerciali o membri della famiglia e assegnare ruoli per controllare cosa possono vedere e gestire.<br /><br />Per verificare la proprietà, contatta il tuo concessionario Volvo Penta autorizzato.",
      },
      action_cta: {
        heading: 'Come agire',
        message: 'Per eseguire una campagna, contatta il tuo concessionario Volvo Penta o OEM.',
        find_dealer: 'Trova il concessionario più vicino',
        learn_more: 'Per saperne di più',
      },
      notifications_cta: {
        heading: 'Notifiche',
        message:
          "Rimani aggiornato e ricevi notifiche via email quando un'unità ha una campagna installabile disponibile.",
        notification_settings: 'Impostazioni notifiche',
      },
    },
    role_types: {
      title: 'Informazioni sui ruoli',
      manager: {
        title: 'Manager',
        description:
          'I manager hanno il pieno controllo amministrativo per supervisionare la flotta, monitorare lo stato delle unità e gestire tutte le unità, le impostazioni e gli accessi degli utenti.',
      },
      technician: {
        title: 'Technician',
        description:
          'Si concentra sulla salute e la manutenzione delle unità. I tecnici possono monitorare i dati delle unità ed eseguire aggiornamenti software da remoto. Questo ruolo non include i permessi di gestione della flotta o degli utenti.',
      },
      viewer: {
        title: 'Viewer',
        description:
          'I visualizzatori hanno accesso in sola lettura. Possono monitorare lo stato delle unità e visualizzare i report, ma non possono modificare impostazioni o configurazioni.',
      },
    },
    campaign_types: {
      title: 'Comprendere i tipi di campagne',
      recall: {
        title: 'Richiamo',
        description:
          'Introduzione retroattiva di una modifica di produzione; dovrebbe essere implementata il prima possibile per garantire il corretto funzionamento del prodotto.',
      },
      quality: {
        title: 'Qualità',
        description:
          'Introduzione retroattiva di una modifica di produzione; dovrebbe essere implementata al prossimo servizio programmato per garantire il corretto funzionamento del prodotto.',
      },
      service: {
        title: 'Servizio',
        description:
          'Introduzione retroattiva di una modifica di produzione relativa alla sicurezza; deve essere implementata immediatamente per garantire un funzionamento sicuro del prodotto.',
      },
      installation: {
        title: 'Installazione',
        description:
          "Un aggiornamento software remoto è pronto per essere installato sulla tua unità utilizzando l'applicazione Volvo Penta.",
        app_store: 'Scaricalo per iOS su App store',
        google_play: 'Scaricalo per Android su Google Play',
      },
    },
  },
};

export default itTranslation;
